import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import { SmartModuleType, SmartModule, TimeSeriesInfo } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchAllHomeSmartModules, fetchAllTimeSeriesInfo, selectAllHomeTimeSeries,
    selectHomeSmartModulesByType, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { capitalizeAll, capitalizeFirst, ITEMS_PER_PAGE_10, ITEMS_PER_PAGE_LIST, GenericMessageBox, PaginationBar,
    PAGING_MIN_PAGES, TimeSeriesTable, TimeSeriesTableProps, TimeSeriesType, useSearchParams } from '@moderepo/mode-app-components';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useNavigate } from 'react-router-dom';
import { useRitzTranslation } from '../../utils/customHooks';
import { SlideOutPanelCompType, TimeSeriesPreviewCompOptions } from '../routes/defaultProjectConfig';
import { RouteHelper } from '../routes/RouteHelper';

/**
 * The set of fields to be hidden from the table. By default, we will show all columns. If we need to hide some column,
 * set column to 'true'
 */
export interface TimeSeriesListHiddenFields {
    readonly id?: boolean;
    readonly homeId?: boolean;
    readonly moduleId?: boolean;
    readonly timeZone?: boolean;
}


/**
 * Required/optional props for this component
 */
export interface TimeSeriesListingContainerProps {
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId: number;
    readonly itemsPerPage?: number | undefined;
    readonly hiddenFields?: TimeSeriesListHiddenFields | undefined;
}


/**
 * This container display all the TIME SERIES belonged to a home. This includes time series from all TSDB modules that a home has.
 */
export const TimeSeriesListingContainer: React.FC<TimeSeriesListingContainerProps> = (
    props: TimeSeriesListingContainerProps,
) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const tsdbSmartModules = selectHomeSmartModulesByType(appDataState, props.homeId, SmartModuleType.TSDB);
    const [selectedTimeSeriesInfo, setSelectedTimeSeriesInfo] = useState<TimeSeriesInfo | undefined>();
    const navigate = useNavigate();

    const {
        searchParams, onItemsPerPageChange, onPrevPage, onNextPage, onPage, onSearchTextChange,
    } = useSearchParams<{}>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {},
    });


    /**
     * Get the home time series info from the data state
     */
    const allHomeTimeSeries = useMemo(() => {
        if (tsdbSmartModules) {
            const result: TimeSeriesInfo[] = [];

            // fetch all collections for each smart module and combine them into 1 array and return all the collections.
            // eslint-disable-next-line no-restricted-syntax
            for (const module of tsdbSmartModules) {
                const moduleTimeSeries = selectAllHomeTimeSeries(appDataState, props.homeId, module.id);
                if (moduleTimeSeries) {
                    result.push(...moduleTimeSeries);
                } else {
                    // If 1 of the modules return undefined then it means the data is not fetched. We will exit right away and wait for
                    // data to be fetched and try again.
                    return undefined;
                }
            }

            return result;
        }
        return undefined;
    }, [appDataState, props.homeId, tsdbSmartModules]);


    // Get user's homes time series API call returns everything so we need to filter from the frontend based on the search text
    const filteredTimeSeries = useMemo(() => {
        if (allHomeTimeSeries) {
            return allHomeTimeSeries.filter((series: TimeSeriesInfo) => {
                return !searchParams.searchText || series.id.toLowerCase().includes(searchParams.searchText);
            });
        }
        return undefined;
    }, [allHomeTimeSeries, searchParams.searchText]);


    // Filter out time series that are not in the view based on pagination value
    const displayTimeSeries = useMemo(() => {
        if (filteredTimeSeries) {
            return filteredTimeSeries.slice(
                searchParams.currentPage * searchParams.itemsPerPage,
                searchParams.currentPage * searchParams.itemsPerPage + searchParams.itemsPerPage,
            );
        }
        return undefined;
    }, [filteredTimeSeries, searchParams.currentPage, searchParams.itemsPerPage]);



    /**
     * This useEffect is used for fetching all the smart modules this home has
     */
    useEffect(() => {
        if (!tsdbSmartModules) {
            appDataDispatch(fetchAllHomeSmartModules(props.homeId)).catch(() => {
                // ignore error
            });
        }
    }, [appDataDispatch, props.homeId, tsdbSmartModules]);


    /**
     * This useEffect is used for fetching time series info
     */
    useEffect(() => {
        if (tsdbSmartModules && tsdbSmartModules.length > 0 && !allHomeTimeSeries) {
            // fetch all time series info for each TSDB smart modules
            tsdbSmartModules.forEach((module: SmartModule) => {
                appDataDispatch(fetchAllTimeSeriesInfo(props.homeId, module.id), true);
            });
            // Reset the pagination to page 0
            onPage(0);
        }
    }, [allHomeTimeSeries, appDataDispatch, onPage, props.homeId, tsdbSmartModules]);



    /**
     * on time series selected, we will show the preview of the time series data in the slide out panel
     */
    const onPreviewTimeSeriesInfo = useCallback((series: TimeSeriesInfo) => {
        setSelectedTimeSeriesInfo(series);
        const options: TimeSeriesPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.TIME_SERIES_PREVIEW,
            compProps: {
                data: {
                    projectId: props.projectId,
                    homeId   : series.homeId,
                    moduleId : series.moduleId,
                    seriesId : series.id,
                },
                context: {
                    userId: props.userId,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, props.userId, uiDispatch]);


    /**
     * On time series selected, go immediately to the historical time series details page.
     */
    const onTimeSeriesSelected = useCallback((series: TimeSeriesInfo) => {
        RouteHelper.getInstance(navigate).gotoHomeHistoricalDataPage({
            projectId        : props.projectId,
            homeId           : props.homeId,
            userId           : props.userId,
            dataId           : series.id,
            tsdbSmartModuleId: series.moduleId,
            timeSeriesType   : TimeSeriesType.SIMPLE,
            dataDisplayName  : series.id,
        });
    }, [navigate, props.homeId, props.projectId, props.userId]);


    /**
     * Define the fields for the time series table and cache it.
     */
    const timeSeriesTableFields = useMemo((): TimeSeriesTableProps['fieldsSettings'] => {
        return {
            fields: {
                preview: {
                    label: '',      // The column doesn't need a label so we can leave it blank
                },
                id: {
                    label       : capitalizeAll(trans('label.id')),
                    dataItemProp: 'id',
                    hidden      : props.hiddenFields?.id,
                },
                homeId: {
                    label       : capitalizeAll(trans('label.home_id')),
                    dataItemProp: 'homeId',
                    hidden      : props.hiddenFields?.homeId,
                },
                moduleId: {
                    label       : capitalizeAll(trans('label.tsdb_smart_module_id')),
                    dataItemProp: 'moduleId',
                    hidden      : props.hiddenFields?.moduleId,
                },
                timeZone: {
                    label       : capitalizeAll(trans('label.time_zone')),
                    dataItemProp: 'timeZone',
                    hidden      : props.hiddenFields?.timeZone,
                },
            },
        };
    }, [props.hiddenFields, trans]);


    /**
     * convert filteredTimeSeries to TimeSeriesTableDataItem
     */
    const localizedTimeSeriesTableData = useMemo((): TimeSeriesTableProps['listData'] => {
        if (displayTimeSeries) {
            return displayTimeSeries.map((series: TimeSeriesInfo) => {
                return {
                    actualValue : series,
                    canBeRemoved: true,
                    displayValue: {
                        id      : series.id,
                        homeId  : series.homeId.toString(),
                        moduleId: series.moduleId,
                        timeZone: series.timeZone,
                    },
                    selected: selectedTimeSeriesInfo?.id === series.id,
                };
            });
        }

        return [];
    }, [displayTimeSeries, selectedTimeSeriesInfo]);


    const timeSeriesTablePaginationSettings = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : filteredTimeSeries ? filteredTimeSeries.length : undefined,
                hasMoreItems    : filteredTimeSeries !== undefined
                    && filteredTimeSeries.length > ((searchParams.currentPage + 1) * searchParams.itemsPerPage),
                texts: {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('terms.home', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [searchParams.itemsPerPage, searchParams.currentPage, filteredTimeSeries, trans, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);


    return (
        <>
            {tsdbSmartModules && displayTimeSeries && (
                <TimeSeriesTable
                    icon="storage"
                    title={trans('panel.time_series_list.title')}
                    fieldsSettings={timeSeriesTableFields}
                    listData={localizedTimeSeriesTableData}
                    texts={{
                        searchBox: {
                            placeholder: capitalizeFirst(trans('label.ts_search_placeholder')),
                        },
                    }}
                    enableSearch={allHomeTimeSeries && allHomeTimeSeries.length > 0}
                    onSearchTextChanged={onSearchTextChange}
                    paginationComp={timeSeriesTablePaginationSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.time_series', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.time_series', { count: 2 })),
                            })}
                        />
                    )}
                    onPreviewItemClicked={onPreviewTimeSeriesInfo}
                    onListItemSelected={onTimeSeriesSelected}
                />
            )}

            {(!tsdbSmartModules || !displayTimeSeries) && (
                <CircularProgress />
            )}
        </>
    );
};
